import { router, Stack } from 'expo-router';
import { useAtomValue } from 'jotai';
import { type DataTableRowEditCompleteEvent } from 'primereact/datatable';
import React, { useEffect, useState } from 'react';
import { useToastController, YStack } from 'smoothie';

import { FullPageContainer } from '../../common/components/GroveContainers';
import { GroveLogo } from '../../common/components/GroveLogo';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { adminUserAtom, allGuidesAtom } from '../../entities/entityAtoms';
import {
    useCreateGuideAndRefetch,
    useUpdateGuideAndRefetch,
} from '../hooks/useGuideMutations';
import type { CreateGuideDto, UpdateGuideDto } from '../types/GuideTypes';
import { GuideModal } from './GuideModal';
import { GuideTable } from './GuideTable';

export const GuideManagementPage = () => {
    const adminUser = useAtomValue(adminUserAtom);
    const canAccessGuides = adminUser.authorization.guides.canViewAll;
    const canEditGuides = adminUser.authorization.guides.canEditAll;

    // Get guides from the existing GuideEntityProvider
    const guides = useAtomValue(allGuidesAtom);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const { createGuideAsync, isCreating } = useCreateGuideAndRefetch();
    const { updateGuideAsync } = useUpdateGuideAndRefetch();

    const toast = useToastController();

    // Redirect if user doesn't have access
    useEffect(() => {
        if (!canAccessGuides) {
            router.replace('/');
        }
    }, [canAccessGuides]);

    const handleCreateGuide = async (data: CreateGuideDto) => {
        if (!canEditGuides) return;
        await createGuideAsync(data);
        setIsCreateModalOpen(false);
    };

    const handleRowEditComplete = async ({
        newData,
        index,
    }: DataTableRowEditCompleteEvent) => {
        if (!canEditGuides) return;

        const guideId = guides[index]?.id;
        if (guideId == null) return;

        const guide: UpdateGuideDto = newData as UpdateGuideDto;

        // Convert the edited data to UpdateGuideDto format
        const updateData: UpdateGuideDto = {
            status: guide.status,
            pronouns: guide.pronouns,
            signUpId: guide.signUpId,
            profileImageUrl: guide.profileImageUrl,
            bio: guide.bio,
            styles: guide.styles,
            oooStart: guide.oooStart ?? null,
            oooEnd: guide.oooEnd ?? null,
            advisorId: guide.advisorId ?? null,
            firefliesApiKey: guide.firefliesApiKey,
        };

        try {
            await updateGuideAsync(guideId, updateData);
            toast.show('Updated guide!', MutationSuccessToastOptions);
        } catch (err) {
            toast.show('Error updating guide', MutationErrorToastOptions());
        }
    };

    return (
        <>
            <Stack.Screen
                options={{
                    headerLeft: () => (
                        <YStack marginHorizontal={16}>
                            <GroveLogo />
                        </YStack>
                    ),
                    headerTitle: 'Guide Management',
                    headerTitleAlign: 'left',
                    headerBackVisible: true,
                }}
            />
            <FullPageContainer marginVertical={24}>
                <GuideTable
                    guides={guides}
                    onCreateNew={() => {
                        if (canEditGuides) {
                            setIsCreateModalOpen(true);
                        }
                    }}
                    onRowEditComplete={handleRowEditComplete}
                />
            </FullPageContainer>

            {canEditGuides && (
                <GuideModal
                    isOpen={isCreateModalOpen}
                    onClose={() => {
                        setIsCreateModalOpen(false);
                    }}
                    onSubmit={handleCreateGuide}
                    isSubmitting={isCreating}
                />
            )}
        </>
    );
};
